import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ColumnMapperField, ColumnMapping } from '../../interfaces/column-mapper-field.interface';

/**
 * Generic "map a spreadsheet column to a target field" UI, with an optional per-field
 * value-translation step. Domain-agnostic: callers supply the field definitions and read the
 * resulting mapping themselves, so this can back any Excel/CSV import.
 */
@Component({
    selector: 'app-excel-column-mapper',
    templateUrl: './excel-column-mapper.component.html',
    styleUrl: './excel-column-mapper.component.scss',
    standalone: false,
})
export class ExcelColumnMapperComponent implements OnChanges {
    @Input() columns: string[] = [];
    @Input() rows: Record<string, unknown>[] = [];
    @Input() fields: ColumnMapperField[] = [];
    @Input() initialMapping?: Record<string, string>;

    @Output() mappingChange = new EventEmitter<ColumnMapping>();
    // Emits true whenever the same column is mapped to more than one field - callers should
    // block saving while this is true, since only one of the conflicting fields would actually
    // get the column's data.
    @Output() mappingConflict = new EventEmitter<boolean>();

    public fieldColumns: Record<string, string | null> = {};
    public fieldUniqueValues: Record<string, string[]> = {};
    public fieldValueMaps: Record<string, Record<string, string>> = {};

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.fields || changes.initialMapping) {
            this.fieldColumns = {};
            this.fieldValueMaps = {};
            this.fieldUniqueValues = {};

            for (const field of this.fields) {
                const column = this.initialMapping?.[field.key] ?? null;
                this.fieldColumns[field.key] = column;

                if (column) {
                    this.recomputeValueOptions(field, column);
                }
            }

            this.emitMapping();
            return;
        }

        if (changes.rows || changes.columns) {
            for (const field of this.fields) {
                const column = this.fieldColumns[field.key];

                if (column) {
                    this.recomputeValueOptions(field, column);
                }
            }
        }
    }

    public onColumnSelected(field: ColumnMapperField, column: string | null): void {
        this.fieldColumns[field.key] = column || null;

        if (column) {
            this.recomputeValueOptions(field, column);
        } else {
            delete this.fieldUniqueValues[field.key];
            delete this.fieldValueMaps[field.key];
        }

        this.emitMapping();
    }

    public onValueMapped(fieldKey: string, rawValue: string, mappedValue: string | null): void {
        const valueMap = { ...(this.fieldValueMaps[fieldKey] ?? {}) };

        if (mappedValue) {
            valueMap[rawValue] = mappedValue;
        } else {
            delete valueMap[rawValue];
        }

        this.fieldValueMaps[fieldKey] = valueMap;
        this.emitMapping();
    }

    private recomputeValueOptions(field: ColumnMapperField, column: string): void {
        if (!field.valueOptions?.length) {
            return;
        }

        const uniqueValues = new Set<string>();

        for (const row of this.rows) {
            const value = row[column];

            if (value !== null && value !== undefined && value !== '') {
                uniqueValues.add(String(value));
            }
        }

        this.fieldUniqueValues[field.key] = Array.from(uniqueValues);

        const valueMap = { ...(this.fieldValueMaps[field.key] ?? {}) };

        for (const rawValue of uniqueValues) {
            if (valueMap[rawValue] === undefined) {
                const match = field.valueOptions.find(option => option.toLowerCase() === rawValue.toLowerCase());

                if (match) {
                    valueMap[rawValue] = match;
                }
            }
        }

        this.fieldValueMaps[field.key] = valueMap;
    }

    // The field whose column also got picked for a different field, if any - used both to warn
    // the user and to decide whether saving should be blocked.
    public getConflictingField(field: ColumnMapperField): ColumnMapperField | null {
        const column = this.fieldColumns[field.key];

        if (!column) {
            return null;
        }

        return this.fields.find(other => other.key !== field.key && this.fieldColumns[other.key] === column) ?? null;
    }

    private hasConflict(): boolean {
        return this.fields.some(field => this.getConflictingField(field) !== null);
    }

    private emitMapping(): void {
        const mapping: ColumnMapping = {};

        for (const field of this.fields) {
            mapping[field.key] = {
                column: this.fieldColumns[field.key] ?? null,
                valueMap: field.valueOptions?.length ? (this.fieldValueMaps[field.key] ?? {}) : undefined,
            };
        }

        this.mappingChange.emit(mapping);
        this.mappingConflict.emit(this.hasConflict());
    }
}
